import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Card } from '@/components/ui/card'
import { cn } from '@/utils/cn'

export interface KpiStatTileProps {
  icon: LucideIcon
  label: string
  value: ReactNode | string | number | null | undefined
  trend?: string
  secondary?: string
  highlight?: boolean
  className?: string
}

function renderValue(value: KpiStatTileProps['value']): ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/40">—</span>
  }
  if (typeof value === 'number') {
    return value.toLocaleString()
  }
  return value
}

export function KpiStatTile({
  icon: Icon,
  label,
  value,
  trend,
  secondary,
  highlight = false,
  className = '',
}: KpiStatTileProps) {
  return (
    <Card className={cn('px-4 py-3', className)}>
      {/* Top row: icon on left, trend on right */}
      <div className="flex items-center justify-between mb-2">
        <Icon className={cn('size-4', highlight ? 'text-primary' : 'text-muted-foreground')} />
        {trend && <span className="text-xs font-medium text-primary">{trend}</span>}
      </div>
      {/* Label */}
      <div className="text-3xs font-medium uppercase tracking-widest text-muted-foreground/70 mb-1">
        {label}
      </div>
      {/* Value */}
      <div
        className={cn(
          'text-2xl font-bold tracking-tight tabular-nums',
          highlight ? 'text-primary' : 'text-foreground'
        )}
      >
        {renderValue(value)}
      </div>
      {/* Secondary description */}
      {secondary && <div className="mt-1 text-xs text-muted-foreground">{secondary}</div>}
    </Card>
  )
}
